import React, { useMemo, useState } from 'react'
import {
  GoogleDrivePickerItem,
  GoogleDriveResolvedItem
} from '../types'
import { parseGoogleDriveUrls } from '../utils'
import {
  ConnectionCard,
  ConnectionTitle,
  ActionButton,
  ButtonGroup,
  FormGroup,
  Label,
  InputField,
  Checkbox,
  CheckboxLabel,
  ErrorMessage
} from '../style'

declare global {
  interface Window {
    gapi: any
    google: any
  }
}

type Tab = 'browse' | 'paste'

interface GoogleDriveAddModalProps {
  isOpen: boolean
  defaultDataRoomPath: string
  isConnected?: boolean
  connectedEmail?: string
  checklistKey?: string
  checklistLabel?: string
  onClose: () => void
  onConnect?: () => void
  onResolveUrl: (url: string) => Promise<GoogleDriveResolvedItem | undefined>
  onLinkItems: (
    items: Array<GoogleDrivePickerItem | GoogleDriveResolvedItem>,
    dataRoomPath: string,
    mirrorStructure: boolean,
    checklistKey?: string
  ) => Promise<void>
  onImportItems: (
    items: Array<GoogleDrivePickerItem | GoogleDriveResolvedItem>,
    dataRoomPath: string,
    mirrorStructure: boolean,
    checklistKey?: string
  ) => Promise<void>
  onSyncFolders?: (
    folders: Array<GoogleDrivePickerItem | GoogleDriveResolvedItem>,
    dataRoomPath: string,
    syncSubfolders: boolean,
    checklistKey?: string
  ) => Promise<void>
}

const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file'
const FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder'

function getPickerConfig() {
  return {
    clientId: process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID || process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
    apiKey: process.env.REACT_APP_GOOGLE_DRIVE_API_KEY || '',
    appId: process.env.REACT_APP_GOOGLE_DRIVE_APP_ID || ''
  }
}

function loadScript(id: string, src: string): Promise<void> {
  if (document.getElementById(id)) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = id
    script.src = src
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.body.appendChild(script)
  })
}

async function ensurePickerLoaded(): Promise<void> {
  await Promise.all([
    loadScript('google-api-js', 'https://apis.google.com/js/api.js'),
    loadScript('google-identity-services-js', 'https://accounts.google.com/gsi/client')
  ])

  await new Promise<void>((resolve, reject) => {
    if (!window.gapi) {
      reject(new Error('Google API loader is unavailable'))
      return
    }
    window.gapi.load('picker', {
      callback: () => resolve(),
      onerror: () => reject(new Error('Google Picker failed to load'))
    })
  })
}

function normalizePickerDocument(doc: any): GoogleDrivePickerItem {
  const picker = window.google?.picker
  const id = doc[picker.Document.ID]
  const name = doc[picker.Document.NAME]
  const mimeType = doc[picker.Document.MIME_TYPE]
  const url = doc[picker.Document.URL]
  return {
    id,
    name,
    mimeType,
    url,
    kind: mimeType === FOLDER_MIME_TYPE ? 'folder' : 'file'
  }
}

export const GoogleDriveAddModal: React.FC<GoogleDriveAddModalProps> = ({
  isOpen,
  defaultDataRoomPath,
  isConnected = true,
  connectedEmail,
  checklistKey,
  checklistLabel,
  onClose,
  onConnect,
  onResolveUrl,
  onLinkItems,
  onImportItems,
  onSyncFolders
}) => {
  const [tab, setTab] = useState<Tab>('browse')
  const [pasteValue, setPasteValue] = useState('')
  const [items, setItems] = useState<Array<GoogleDrivePickerItem | GoogleDriveResolvedItem>>([])
  const [dataRoomPath, setDataRoomPath] = useState(defaultDataRoomPath || '/')
  const [mirrorStructure, setMirrorStructure] = useState(true)
  const [syncSubfolders, setSyncSubfolders] = useState(true)
  const [savingMode, setSavingMode] = useState<'link' | 'import' | undefined>()
  const [isResolving, setIsResolving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  React.useEffect(() => {
    if (isOpen) {
      setTab('browse')
      setPasteValue('')
      setItems([])
      setDataRoomPath(defaultDataRoomPath || '/')
      setMirrorStructure(true)
      setSyncSubfolders(true)
      setSavingMode(undefined)
      setIsResolving(false)
      setError(null)
    }
  }, [isOpen, defaultDataRoomPath])

  const parsedUrls = useMemo(() => parseGoogleDriveUrls(pasteValue), [pasteValue])
  const invalidUrlCount = parsedUrls.filter(row => !row.parsed).length

  const openPicker = async () => {
    if (!isConnected) {
      setError('Connect Google Drive before selecting files or folders.')
      return
    }

    const config = getPickerConfig()
    if (!config.clientId || !config.apiKey || !config.appId) {
      setError('Google Picker is not configured. Add the Drive client ID, API key, and app ID to the frontend environment.')
      return
    }

    setError(null)
    try {
      await ensurePickerLoaded()
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: config.clientId,
        scope: GOOGLE_DRIVE_SCOPE,
        callback: (tokenResponse: any) => {
          if (tokenResponse.error) {
            setError(tokenResponse.error)
            return
          }
          const picker = window.google.picker
          const docsView = new picker.DocsView()
            .setIncludeFolders(true)
            .setSelectFolderEnabled(true)
            .setMode(picker.DocsViewMode.LIST)

          const drivePicker = new picker.PickerBuilder()
            .addView(docsView)
            .enableFeature(picker.Feature.MULTISELECT_ENABLED)
            .setOAuthToken(tokenResponse.access_token)
            .setDeveloperKey(config.apiKey)
            .setAppId(config.appId)
            .setCallback((data: any) => {
              if (data[picker.Response.ACTION] === picker.Action.PICKED) {
                const selected: GoogleDrivePickerItem[] = data[picker.Response.DOCUMENTS].map(normalizePickerDocument)
                setItems(prev => {
                  const seen = new Set(prev.map(item => item.id))
                  return prev.concat(selected.filter(item => !seen.has(item.id)))
                })
              }
            })
            .build()
          drivePicker.setVisible(true)
        }
      })
      tokenClient.requestAccessToken({ prompt: 'consent' })
    } catch (err: any) {
      setError(err.message || 'Failed to open Google Picker')
    }
  }

  const resolvePastedUrls = async () => {
    if (!isConnected) {
      setError('Connect Google Drive before resolving pasted links.')
      return
    }

    if (invalidUrlCount > 0) {
      setError('Remove unsupported Google Drive links before resolving.')
      return
    }
    if (parsedUrls.length === 0) {
      setError('Paste at least one Google Drive link.')
      return
    }

    setIsResolving(true)
    setError(null)
    try {
      const resolved = await Promise.all(parsedUrls.map(row => onResolveUrl(row.url)))
      setItems(prev => {
        const seen = new Set(prev.map(item => item.id))
        return prev.concat(resolved.filter(Boolean).filter(item => !seen.has(item!.id)) as GoogleDriveResolvedItem[])
      })
    } catch (err: any) {
      setError(err.message || 'Could not resolve one or more Drive links. Open Picker to grant this app access to the file or folder.')
    } finally {
      setIsResolving(false)
    }
  }

  const save = async (mode: 'link' | 'import') => {
    if (items.length === 0) {
      setError('Choose at least one Google Drive file or folder.')
      return
    }
    if (!isConnected) {
      setError('Connect Google Drive before saving Drive links.')
      return
    }
    if (!dataRoomPath.trim()) {
      setError('Choose a Data Room destination path.')
      return
    }

    setSavingMode(mode)
    setError(null)
    try {
      const folderItems = items.filter(item => item.kind === 'folder')
      const fileItems = items.filter(item => item.kind !== 'folder')

      if (folderItems.length > 0) {
        if (!onSyncFolders) {
          setError('Drive folders must be saved as sync configurations from the Google Drive sync page.')
          return
        }
        await onSyncFolders(folderItems, dataRoomPath.trim(), syncSubfolders, checklistKey)
      }

      if (fileItems.length > 0) {
        if (mode === 'link') {
          await onLinkItems(fileItems, dataRoomPath.trim(), mirrorStructure, checklistKey)
        } else {
          await onImportItems(fileItems, dataRoomPath.trim(), mirrorStructure, checklistKey)
        }
      }
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save Google Drive links.')
    } finally {
      setSavingMode(undefined)
    }
  }

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.72)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '24px'
    }}>
      <ConnectionCard style={{ maxWidth: '720px', width: '100%', maxHeight: '86vh', overflow: 'auto' }}>
        <ConnectionTitle>Add from Google Drive</ConnectionTitle>
        {checklistLabel && (
          <p style={{ color: '#aaa', marginTop: '8px' }}>
            Checklist item: {checklistLabel}
          </p>
        )}

        {!isConnected ? (
          <div style={{
            border: '1px solid #4285f4',
            borderRadius: '6px',
            padding: '12px',
            color: '#ddd',
            marginTop: '16px'
          }}>
            <div style={{ marginBottom: '10px' }}>Connect a Google account before adding Drive files to the Data Room.</div>
            {onConnect && <ActionButton onClick={onConnect}>Connect Google Drive</ActionButton>}
          </div>
        ) : connectedEmail ? (
          <div style={{ color: '#aaa', marginTop: '10px' }}>Connected as {connectedEmail}</div>
        ) : null}

        {error && <ErrorMessage style={{ margin: '16px 0' }}>{error}</ErrorMessage>}

        <ButtonGroup style={{ margin: '18px 0' }}>
          <ActionButton variant={tab === 'browse' ? 'primary' : 'secondary'} onClick={() => setTab('browse')}>Browse Drive</ActionButton>
          <ActionButton variant={tab === 'paste' ? 'primary' : 'secondary'} onClick={() => setTab('paste')}>Paste link</ActionButton>
        </ButtonGroup>

        {tab === 'browse' ? (
          <FormGroup style={{ marginBottom: '16px' }}>
            <Label>Choose files or folders</Label>
            <ActionButton onClick={openPicker} disabled={!isConnected}>Open Google Picker</ActionButton>
          </FormGroup>
        ) : (
          <FormGroup style={{ marginBottom: '16px' }}>
            <Label>Paste Google Drive links</Label>
            <textarea
              value={pasteValue}
              onChange={e => setPasteValue(e.target.value)}
              placeholder={'One Google Drive URL per line'}
              style={{
                minHeight: '120px',
                resize: 'vertical',
                borderRadius: '6px',
                padding: '12px',
                background: '#141428',
                color: 'white',
                border: '1px solid #333'
              }}
            />
            <div style={{ color: invalidUrlCount > 0 ? '#f44336' : '#aaa', fontSize: '0.8rem' }}>
              {parsedUrls.length} link{parsedUrls.length === 1 ? '' : 's'} detected
              {invalidUrlCount > 0 ? `, ${invalidUrlCount} unsupported` : ''}
            </div>
            <ActionButton onClick={resolvePastedUrls} disabled={isResolving || !isConnected}>
              {isResolving ? 'Resolving...' : 'Resolve links'}
            </ActionButton>
          </FormGroup>
        )}

        <FormGroup style={{ marginBottom: '16px' }}>
          <Label>Data Room destination</Label>
          <InputField value={dataRoomPath} onChange={e => setDataRoomPath(e.target.value)} placeholder="/" />
        </FormGroup>

        {items.some(item => item.kind !== 'folder') && (
          <CheckboxLabel style={{ marginBottom: '16px' }}>
            <Checkbox checked={mirrorStructure} onChange={e => setMirrorStructure(e.target.checked)} />
            Mirror Drive folder structure
          </CheckboxLabel>
        )}

        {items.some(item => item.kind === 'folder') && (
          <CheckboxLabel style={{ marginBottom: '16px' }}>
            <Checkbox checked={syncSubfolders} onChange={e => setSyncSubfolders(e.target.checked)} />
            Include subfolders
          </CheckboxLabel>
        )}

        <div style={{ marginBottom: '20px', color: '#ddd' }}>
          <strong>{items.length}</strong> selected
          {items.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: '20px', maxHeight: '140px', overflow: 'auto' }}>
              {items.map(item => (
                <li key={item.id}>{item.name || item.id} <span style={{ color: '#aaa' }}>({item.kind})</span></li>
              ))}
            </ul>
          )}
        </div>

        <ButtonGroup style={{ justifyContent: 'flex-end' }}>
          <ActionButton variant="secondary" onClick={onClose} disabled={!!savingMode}>Cancel</ActionButton>
          <ActionButton variant="secondary" onClick={() => save('link')} disabled={!!savingMode || items.length === 0 || !isConnected}>
            {savingMode === 'link' ? 'Saving...' : 'Link only'}
          </ActionButton>
          <ActionButton onClick={() => save('import')} disabled={!!savingMode || items.length === 0 || !isConnected}>
            {savingMode === 'import' ? 'Syncing...' : 'Add and sync'}
          </ActionButton>
        </ButtonGroup>
      </ConnectionCard>
    </div>
  )
}
